import {useQuery} from "@apollo/client";
import {
  faArrowUpRightFromSquare,
  faBook,
  faExclamationTriangle,
  faImage,
  faStar,
  faTents,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Alert, Button, Card, Group, Tabs, Text} from "@mantine/core";
import classNames from "classnames";
import dayjs from "dayjs";
import parse from "html-react-parser";
import {isEmpty, noop} from "lodash";
import Link from "next/link";
import {useMemo} from "react";

import classes from "./Popover.module.scss";
import {DEFAULT_SLOTS_DAYS} from "../../constants";
import * as queries from "../../graphql/queries";
import type {Facility, Watch} from "../../graphql/types";
import {searchStateToQueryString} from "../../lib/search";
import {SearchPopoverProps} from "../../lib/types";
import {getMapUrl} from "../../lib/utils";
import ReserveFacilityButton from "../buttons/ReserveFacilityButton";
import WatchButton from "../buttons/WatchButton";
import FacilityUnitsTable from "../FacilityUnitsTable/FacilityUnitsTable";
import Loader from "../Loader";
import MediaCarousel from "../MediaCarousel";
import Rating from "../Rating";
import Reviews from "../Reviews";

// Available tabs
type Tab = "details" | "photos" | "units";

export interface FacilityPopoverProps extends SearchPopoverProps {
  facility: Partial<Facility>; // Facility to display in popover
  defaultTab?: Tab; // Default tab to display when opened
  onWatchUpdated?(watch: Watch): void; // Callback to invoke when this Facility's Watch is updated
  filtersApplied?: boolean; // Should user's search filters be applied to units?
}

/**
 * Facility popover
 *
 * Displays when a user clicks a facility map marker
 */
export default function FacilityPopover({
  facility: initialFacility,
  defaultTab,
  searchInput: initialSearchInput,
  onClose = noop,
  onWatchUpdated = noop,
  filtersApplied,
}: FacilityPopoverProps) {
  // Add default dates
  const searchInput = useMemo(() => {
    const dates = initialSearchInput?.filter?.dates || {};
    return {
      ...(initialSearchInput || {}),
      filter: {
        ...(initialSearchInput?.filter || {}),
        onlyTypes: ["Unit"],
        dates: {
          min: dates.min || dayjs().format("YYYY-MM-DD"),
          max:
            dates.max ||
            dayjs().add(DEFAULT_SLOTS_DAYS, "days").format("YYYY-MM-DD"),
        },
      },
    };
  }, [initialSearchInput]);

  if (filtersApplied === undefined) {
    filtersApplied = !isEmpty(initialSearchInput?.filter);
  }

  const {
    loading,
    error,
    data: {entities: {facility: facilityDetails = undefined} = {}} = {},
  } = useQuery(queries.GET_FACILITY_DETAILS, {
    variables: {
      id: initialFacility.id,
      slug: initialFacility.slug,
    },
  });

  const facility: Partial<Facility> = facilityDetails || initialFacility;

  let facilityUrl = `/facilities/${facility?.slug}`;
  let parkUrl = `/parks/${facility?.park?.slug}`;
  if (filtersApplied) {
    const queryString = searchStateToQueryString({searchInput});
    facilityUrl += `?${queryString}`;
    parkUrl += `?${queryString}`;
  }

  const facilityName = facility.name || `Unnamed #${facility.id}`;

  let description = facility.descriptionHtml
    ? parse(facility.descriptionHtml || "")
    : facility.description;

  if (description === facility.name) {
    description = null;
  }

  const mapUrl = facility.location ? getMapUrl(facility.location) : null;

  if (!defaultTab) {
    defaultTab = "units";
  }

  let cardBody;
  if (loading) {
    cardBody = <Loader centered />;
  } else if (error) {
    cardBody = (
      <Alert
        variant="filled"
        color="red"
        icon={<FontAwesomeIcon icon={faExclamationTriangle} />}>
        {error.message}
      </Alert>
    );
  } else {
    cardBody = (
      <Tabs defaultValue={defaultTab}>
        <Tabs.List>
          <Tabs.Tab
            value="details"
            leftSection={<FontAwesomeIcon icon={faBook} />}>
            Info
          </Tabs.Tab>
          {!isEmpty(facility.media) && (
            <Tabs.Tab
              value="photos"
              leftSection={<FontAwesomeIcon icon={faImage} />}>
              Photos
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value="reviews"
            leftSection={<FontAwesomeIcon icon={faStar} />}>
            Reviews
          </Tabs.Tab>
          <Tabs.Tab
            value="units"
            leftSection={<FontAwesomeIcon icon={faTents} />}>
            Sites
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" className={classes.tabContent}>
          {description ? (
            <div className="embedded-html">
              {facility.descriptionHtml
                ? parse(facility.descriptionHtml)
                : facility.description || ""}
            </div>
          ) : (
            <Text size="sm">No description available.</Text>
          )}
        </Tabs.Panel>

        {!isEmpty(facility.media) && (
          <Tabs.Panel value="photos" className={classes.tabContent}>
            <MediaCarousel small mediaList={facility.media || []} />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="reviews" className={classes.tabContent}>
          <Group gap="xl" align="flex-start">
            {facility.rating?.average && <Rating rating={facility.rating} />}
            <Reviews
              results={facility.reviews}
              forType="Facility"
              idSelector={{facilityId: facility.id}}
            />
          </Group>
        </Tabs.Panel>

        <Tabs.Panel value="units" className={classes.tabContent}>
          <FacilityUnitsTable
            facility={facility}
            searchInput={searchInput}
            linkTarget="_blank"
            filtersApplied={filtersApplied}
          />
        </Tabs.Panel>
      </Tabs>
    );
  }

  return (
    <Card className={classNames(classes.popover, "facility-popover")}>
      <Card.Section className={classes.header}>
        <h4>
          <a
            className={classes.closeButton}
            href="#"
            title="Close"
            onClick={event => {
              event.preventDefault();
              onClose();
            }}>
            <FontAwesomeIcon icon={faXmark} size="2xs" />
          </a>
          <div className={classes.parent}>
            {facility.park ? (
              <Link
                href={parkUrl}
                title={facility.park.name || ""}
                legacyBehavior
                passHref
                className={classNames({
                  [classes.disabledLink]: !facility.park.slug,
                })}
                aria-disabled={!facility.park.slug}>
                <a target="_blank">{facility.park.name}</a>
              </Link>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>
          <Link
            href={facilityUrl}
            title={facilityName}
            legacyBehavior
            passHref
            className={classNames({[classes.disabledLink]: !facility.slug})}
            aria-disabled={!facility.slug}>
            <a target="_blank">{facilityName}</a>
          </Link>
        </h4>
      </Card.Section>
      <Card.Section className={classes.body}>{cardBody}</Card.Section>
      {!loading && (
        <Card.Section className={classes.footer}>
          <Button.Group>
            <ReserveFacilityButton
              facility={facility}
              size="sm"
              source="facility_popover"
            />
            <WatchButton target={facility} onWatchUpdated={onWatchUpdated} />
          </Button.Group>
          {mapUrl && (
            <Button
              component="a"
              variant="default"
              title="Open in Maps"
              leftSection={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
              href={mapUrl}
              target="_blank">
              Map
            </Button>
          )}
        </Card.Section>
      )}
    </Card>
  );
}
